import { useState } from 'react';
import { Link2, Search, Loader2, CheckCircle2, Unlink, ShieldAlert, Send, Sparkles, Clock, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import ModalDesvincular from './ModalDesvincular';

const JUSTIFICATIVA_MINIMA = 10;

/** Etiqueta de status do vínculo (usada na lista de correspondência e no cabeçalho do item). */
export function EtiquetaVinculo({ item }) {
  if (item.vinculo && item.desvinculoPendente) {
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">Desvínculo solicitado</span>;
  }
  if (item.vinculo) {
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-100 text-green-700">ERP {item.vinculo.codigoErp}</span>;
  }
  if (item.solicitacaoPendente) {
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-amber-100 text-amber-700">Aguardando admin</span>;
  }
  if (item.sugestaoErp) {
    return <span className="px-2 py-0.5 rounded text-xs font-bold bg-blue-100 text-blue-700">Sugerido pelo EAN</span>;
  }
  return <span className="px-2 py-0.5 rounded text-xs font-bold bg-gray-200 text-gray-600">Sem vínculo</span>;
}

function dadosDoItem(item, fornecedor, codigoErp) {
  return {
    fornecedorCnpj: fornecedor.cnpj,
    fornecedorNome: fornecedor.nome,
    codigoFornecedor: item.codigo,
    ean: item.ean,
    descricaoFornecedor: item.descricao,
    codigoErp,
  };
}

/** Caixa de conflito: código do ERP já pertence a outra razão social. Admin libera; gerente solicita. */
function ConflitoOutroFornecedor({ conflito, produto, ehAdmin, enviando, onLiberar, onSolicitar, onCancelar, onDesvincularOutro }) {
  const [justificativa, setJustificativa] = useState('');
  const valida = justificativa.trim().length >= JUSTIFICATIVA_MINIMA;

  return (
    <div className="rounded-lg border-2 border-red-300 bg-red-50 p-3 space-y-2">
      <p className="flex items-start gap-2 text-sm text-red-800">
        <ShieldAlert className="w-5 h-5 flex-shrink-0" />
        <span>
          O código <strong>{produto.codigo}</strong> ({produto.nome}) já pertence a{' '}
          <strong>{conflito.fornecedorNome || conflito.fornecedorCnpj}</strong> — produto {conflito.codigoFornecedor}
          {conflito.descricaoFornecedor ? ` "${conflito.descricaoFornecedor}"` : ''}.
          {' '}Cada produto de cada razão social só pode ter um código no ERP: <strong>escolha outro código
          ou cadastre um produto novo no ShopControl</strong>.
        </span>
      </p>
      <textarea
        value={justificativa}
        onChange={e => setJustificativa(e.target.value)}
        rows={2}
        placeholder={ehAdmin
          ? 'Se for mesmo a mesma mercadoria, explique por que liberar (ex.: mesmo produto comprado de distribuidor)'
          : 'Se for mesmo a mesma mercadoria, explique ao admin por que liberar'}
        className="w-full px-2 py-1 text-sm border border-red-200 rounded-md bg-white"
      />
      <div className="flex flex-wrap gap-2 justify-end">
        <button type="button" onClick={onCancelar} className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md bg-white hover:bg-gray-50">
          Escolher outro código
        </button>
        <button
          type="button"
          onClick={onDesvincularOutro}
          title="Use se o vínculo do outro fornecedor é que está errado"
          className="flex items-center gap-1 px-3 py-1.5 text-sm text-red-700 border border-red-300 rounded-md bg-white hover:bg-red-50"
        >
          <Unlink className="w-4 h-4" /> Desvincular do outro fornecedor
        </button>
        {ehAdmin ? (
          <button
            type="button"
            disabled={!valida || enviando}
            onClick={() => onLiberar(justificativa.trim())}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            Liberar mesmo assim (admin)
          </button>
        ) : (
          <button
            type="button"
            disabled={!valida || enviando}
            onClick={() => onSolicitar(justificativa.trim())}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-amber-600 rounded-md hover:bg-amber-700 disabled:opacity-50"
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Solicitar liberação ao admin
          </button>
        )}
      </div>
      {!valida && <p className="text-[11px] text-red-700">Justificativa com pelo menos {JUSTIFICATIVA_MINIMA} caracteres.</p>}
    </div>
  );
}

/**
 * Correspondência de UM item da nota com um produto do ERP:
 * vinculado → mostra (admin pode desvincular); sugerido pelo EAN → confirmar; sem vínculo → buscar no ERP.
 */
export default function VinculoErp({ item, fornecedor, onAtualizar }) {
  const { isAdmin } = useAuth();
  const ehAdmin = isAdmin();
  const [termo, setTermo] = useState('');
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [conflito, setConflito] = useState(null); // { conflito, produto }
  const [jaVinculado, setJaVinculado] = useState(null); // vínculo atual quando o 409 é "ja_vinculado"
  const [modal, setModal] = useState(null); // { vinculo, outroFornecedor, produtoParaRetentar }

  async function buscar(e, termoBusca = termo) {
    e?.preventDefault();
    if (termoBusca.trim().length < 2) return;
    setBuscando(true);
    try {
      const resp = await apiService.precificacao.produtosErp(termoBusca.trim());
      setResultados(resp.data.data || []);
    } catch (error) {
      console.error('❌ Erro ao buscar produto no ERP:', error);
    } finally {
      setBuscando(false);
    }
  }

  async function vincular(produto, extra = {}) {
    setEnviando(true);
    try {
      const resp = await apiService.precificacao.vincular({ ...dadosDoItem(item, fornecedor, produto.codigo), ...extra });
      onAtualizar(item.indice, { vinculo: resp.data.data, codigoErp: resp.data.data.codigoErp, solicitacaoPendente: false });
      setConflito(null);
      setResultados(null);
      toast.success(`Item ${item.indice + 1} vinculado ao código ${resp.data.data.codigoErp}.`);
    } catch (error) {
      const dados = error.response?.data?.data;
      if (error.response?.status === 409 && dados?.tipo === 'outro_fornecedor') {
        setConflito({ conflito: dados.conflito, produto });
      } else if (error.response?.status === 409 && dados?.tipo === 'ja_vinculado' && dados.vinculoAtual) {
        setJaVinculado(dados.vinculoAtual);
      }
    } finally {
      setEnviando(false);
    }
  }

  async function solicitar(justificativa) {
    setEnviando(true);
    try {
      await apiService.precificacao.solicitarLiberacao({ ...dadosDoItem(item, fornecedor, conflito.produto.codigo), justificativa });
      onAtualizar(item.indice, { solicitacaoPendente: true });
      setConflito(null);
      toast.success('Solicitação enviada ao admin. Acompanhe na aba Vínculos.');
    } catch (error) {
      console.error('❌ Erro ao solicitar liberação:', error);
    } finally {
      setEnviando(false);
    }
  }

  // Vínculo deste próprio item (aparece no "Vinculado" e no erro "ja_vinculado").
  function abrirDesvinculoDoItem(vinculoAtual) {
    setModal({
      vinculo: {
        id: vinculoAtual.id,
        fornecedorNome: fornecedor.nome,
        codigoFornecedor: item.codigo,
        descricaoFornecedor: item.descricao,
        codigoErp: vinculoAtual.codigoErp,
        descricaoErp: vinculoAtual.descricaoErp,
      },
      outroFornecedor: false,
    });
  }

  function abrirDesvinculoDoOutro() {
    const c = conflito.conflito;
    setModal({
      vinculo: {
        id: c.vinculoId,
        fornecedorNome: c.fornecedorNome || c.fornecedorCnpj,
        codigoFornecedor: c.codigoFornecedor,
        descricaoFornecedor: c.descricaoFornecedor,
        codigoErp: c.codigoErp,
        descricaoErp: c.descricaoErp,
      },
      outroFornecedor: true,
      produtoParaRetentar: conflito.produto,
    });
  }

  /** Termo inicial para buscar o código correto: EAN, ou as 2 primeiras palavras da descrição. */
  function termoSugerido() {
    if (item.ean) return item.ean;
    return String(item.descricao || '').split(/\s+/).slice(0, 2).join(' ');
  }

  function aposDesvincular() {
    const retentar = modal?.produtoParaRetentar;
    if (modal?.outroFornecedor) {
      // O código ficou livre: tenta de novo vincular ESTE item ao código escolhido.
      setConflito(null);
      if (retentar) vincular(retentar);
      return;
    }
    onAtualizar(item.indice, { vinculo: null, codigoErp: '', desvinculoPendente: false });
    setJaVinculado(null);
    const t = termoSugerido();
    setTermo(t);
    buscar(null, t);
  }

  function aposSolicitar() {
    if (!modal?.outroFornecedor) onAtualizar(item.indice, { desvinculoPendente: true });
    setJaVinculado(null);
  }

  const modalDesvincular = modal && (
    <ModalDesvincular
      vinculo={modal.vinculo}
      outroFornecedor={modal.outroFornecedor}
      onFechar={() => setModal(null)}
      onDesvinculado={aposDesvincular}
      onSolicitado={aposSolicitar}
    />
  );

  if (item.vinculo) {
    return (
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <CheckCircle2 className="w-4 h-4 text-green-600" />
        <span className="font-mono font-semibold text-green-800">{item.vinculo.codigoErp}</span>
        <span className="text-gray-700 truncate">{item.vinculo.descricaoErp}</span>
        {item.vinculo.liberadoAdmin && <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">LIBERADO PELO ADMIN</span>}
        {item.desvinculoPendente ? (
          <span className="ml-auto flex items-center gap-1 text-xs text-amber-700"><Clock className="w-3.5 h-3.5" /> Aguardando o admin desvincular</span>
        ) : (
          <button
            type="button"
            onClick={() => abrirDesvinculoDoItem(item.vinculo)}
            title={ehAdmin ? 'Desfazer este vínculo para escolher o código correto' : 'Vínculo errado? Peça ao admin para desfazer'}
            className="ml-auto flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
          >
            <Unlink className="w-3.5 h-3.5" /> Desvincular
          </button>
        )}
        {modalDesvincular}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {item.solicitacaoPendente && (
        <p className="flex items-center gap-1 text-xs text-amber-800">
          <Clock className="w-3.5 h-3.5" /> Liberação solicitada ao admin. Enquanto isso, você pode escolher outro código.
        </p>
      )}

      {item.sugestaoErp && !conflito && (
        <div className="flex flex-wrap items-center gap-2 text-sm bg-blue-50 border border-blue-200 rounded-md px-2 py-1.5">
          <Sparkles className="w-4 h-4 text-blue-500" />
          <span>Pelo EAN, parece ser <strong className="font-mono">{item.sugestaoErp.codigo}</strong> — {item.sugestaoErp.nome}</span>
          <button
            type="button"
            disabled={enviando}
            onClick={() => vincular(item.sugestaoErp)}
            className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {enviando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Confirmar vínculo
          </button>
        </div>
      )}

      {jaVinculado && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1 min-w-[200px]">
            Este produto do fornecedor já está vinculado ao código <strong className="font-mono">{jaVinculado.codigoErp}</strong>
            {jaVinculado.descricaoErp ? ` (${jaVinculado.descricaoErp})` : ''}. Se esse vínculo está errado, desvincule para escolher o correto.
          </span>
          <button
            type="button"
            onClick={() => abrirDesvinculoDoItem(jaVinculado)}
            className="flex items-center gap-1 px-3 py-1.5 text-sm text-white bg-red-600 rounded-md hover:bg-red-700"
          >
            <Unlink className="w-4 h-4" /> Desvincular
          </button>
          <button type="button" onClick={() => setJaVinculado(null)} className="px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md bg-white">
            Fechar
          </button>
        </div>
      )}

      {conflito ? (
        <ConflitoOutroFornecedor
          conflito={conflito.conflito}
          produto={conflito.produto}
          ehAdmin={ehAdmin}
          enviando={enviando}
          onLiberar={justificativa => vincular(conflito.produto, { forcar: true, justificativa })}
          onSolicitar={solicitar}
          onCancelar={() => setConflito(null)}
          onDesvincularOutro={abrirDesvinculoDoOutro}
        />
      ) : (
        <>
          <form onSubmit={buscar} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={termo}
                onChange={e => setTermo(e.target.value)}
                placeholder="Buscar no ERP por nome, código ou EAN"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-300 rounded-md"
              />
            </div>
            <button type="submit" disabled={buscando || termo.trim().length < 2} className="px-3 py-1.5 text-sm bg-gray-800 text-white rounded-md disabled:opacity-50">
              {buscando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
            </button>
          </form>

          {resultados && (
            resultados.length === 0 ? (
              <p className="text-xs text-gray-500">Nada encontrado. Se o produto ainda não existe, <strong>cadastre no ShopControl</strong> e busque de novo.</p>
            ) : (
              <ul className="max-h-56 overflow-y-auto divide-y divide-gray-100 border border-gray-200 rounded-md bg-white">
                {resultados.map(p => (
                  <li key={p.codigo} className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-gray-50">
                    <span className="font-mono text-gray-700 w-24 flex-shrink-0 truncate">{p.codigo}</span>
                    <span className="flex-1 min-w-0 truncate">{p.nome}</span>
                    {p.codigoBarras && p.codigoBarras === item.ean && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold">mesmo EAN</span>
                    )}
                    <button
                      type="button"
                      disabled={enviando}
                      onClick={() => vincular(p)}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Vincular
                    </button>
                  </li>
                ))}
              </ul>
            )
          )}
          {!resultados && (
            <p className="text-[11px] text-gray-400">Não encontrou? Cadastre o produto no ShopControl e busque de novo.</p>
          )}
        </>
      )}
      {modalDesvincular}
    </div>
  );
}
